import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import {
  voiceCallsService,
  VoiceCallRow,
  VoiceAgent,
} from "@/services/voiceCallsService";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Hook to fetch voice calls with TanStack Query
 * Automatically refetches when organization changes
 * Includes real-time subscriptions via Supabase
 */
export function useVoiceCallsQuery(): UseQueryResult<VoiceCallRow[], Error> {
  const { organization } = useAuth();
  const organizationId = organization?.id;

  return useQuery({
    queryKey: ["voice-calls", organizationId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("No organization ID available");
      }
      const { data, error } =
        await voiceCallsService.getVoiceCalls(organizationId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000, // 30 seconds - calls update frequently
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
}

/**
 * Hook to fetch voice agents with TanStack Query
 */
export function useVoiceAgentsQuery(): UseQueryResult<VoiceAgent[], Error> {
  const { organization } = useAuth();
  const organizationId = organization?.id;

  return useQuery({
    queryKey: ["voice-agents-list", organizationId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("No organization ID available");
      }
      const { data, error } =
        await voiceCallsService.getVoiceAgents(organizationId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes - agents don't change often
  });
}

/**
 * Hook to get a single voice call by ID
 */
export function useVoiceCallQuery(
  callId: string | null,
): UseQueryResult<VoiceCallRow, Error> {
  return useQuery({
    queryKey: ["voice-call", callId],
    queryFn: async () => {
      if (!callId) {
        throw new Error("No call ID provided");
      }
      const { data, error } = await voiceCallsService.getVoiceCall(callId);
      if (error) throw error;
      if (!data) throw new Error("Call not found");
      return data;
    },
    enabled: !!callId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook to update voice call status
 */
export function useUpdateCallStatusMutation(): UseMutationResult<
  VoiceCallRow,
  Error,
  { callId: string; status: string }
> {
  const queryClient = useQueryClient();
  const { organization } = useAuth();

  return useMutation({
    mutationFn: async ({
      callId,
      status,
    }: {
      callId: string;
      status: string;
    }) => {
      const { data, error } = await voiceCallsService.updateCallStatus(
        callId,
        status,
      );
      if (error) throw error;
      if (!data) throw new Error("No data returned");
      return data;
    },
    onSuccess: (data) => {
      // Invalidate calls list to trigger refetch
      queryClient.invalidateQueries({
        queryKey: ["voice-calls", organization?.id],
      });
      // Update the specific call in cache
      queryClient.setQueryData(["voice-call", data.id], data);
      toast.success("Call status updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update call status: ${error.message}`);
    },
  });
}
